import React, { useState, useMemo } from 'react';

function TransactionsPage() {
  const [inventory, setInventory] = useState([
    { sku: 'FOF-001', name: 'Engine Oil Filter', price: 50.00, stock: 450 },
    { sku: 'BPS-002', name: 'Brake Pads Set', price: 100.00, stock: 320 },
    { sku: 'SPG-003', name: 'Spark Plug', price: 30.00, stock: 280 },
    { sku: 'CSK-004', name: 'Chain Sprocket Kit', price: 150.00, stock: 180 },
    { sku: 'AFT-005', name: 'Air Filter', price: 50.00, stock: 410 },
    { sku: 'CCB-006', name: 'Clutch Cable', price: 35.00, stock: 8 },
    { sku: 'RVM-007', name: 'Rear View Mirror', price: 45.00, stock: 12 },
    { sku: 'HLB-008', name: 'Headlight Bulb', price: 25.00, stock: 15 },
    { sku: 'BAT-009', name: 'Battery 12V', price: 200.00, stock: 6 },
    { sku: 'TTR-010', name: 'Tire 100/90-17', price: 300.00, stock: 95 },
  ]);

  const [transactions, setTransactions] = useState([
    { id: 'TXN-2024-001', date: '6/15/2024', customer: 'Juan Dela Cruz', payment: 'Cash', total: 220, status: 'Completed', items: [{ name: 'Engine Oil Filter', qty: 2, price: 50 }, { name: 'Spark Plug', qty: 4, price: 30 }] },
    { id: 'TXN-2024-002', date: '6/15/2024', customer: 'Maria Santos', payment: 'GCash', total: 100, status: 'Completed', items: [{ name: 'Brake Pads Set', qty: 1, price: 100 }] },
    { id: 'TXN-2024-003', date: '6/14/2024', customer: 'Pedro Garcia', payment: 'Cash', total: 200, status: 'Completed', items: [{ name: 'Chain Sprocket Kit', qty: 1, price: 150 }, { name: 'Air Filter', qty: 1, price: 50 }] },
    { id: 'TXN-2024-004', date: '6/14/2024', customer: 'Ana Reyes', payment: 'Card', total: 50, status: 'Completed', items: [{ name: 'Air Filter', qty: 1, price: 50 }] },
    { id: 'TXN-2024-005', date: '6/13/2024', customer: 'Carlos Mendoza', payment: 'Cash', total: 250, status: 'Completed', items: [{ name: 'Battery 12V', qty: 1, price: 200 }, { name: 'Headlight Bulb', qty: 2, price: 25 }] },
    { id: 'TXN-2024-006', date: '6/13/2024', customer: 'Rosa Torres', payment: 'GCash', total: 600, status: 'Completed', items: [{ name: 'Tire 100/90-17', qty: 2, price: 300 }] },
    { id: 'TXN-2024-007', date: '6/12/2024', customer: 'Luis Ramos', payment: 'Cash', total: 125, status: 'Completed', items: [{ name: 'Rear View Mirror', qty: 1, price: 45 }, { name: 'Clutch Cable', qty: 2, price: 35 }] },
    { id: 'TXN-2024-008', date: '6/12/2024', customer: 'Elena Cruz', payment: 'Card', total: 120, status: 'Pending', items: [{ name: 'Spark Plug', qty: 4, price: 30 }] },
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTx, setSelectedTx] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Modal state
  const [customerName, setCustomerName] = useState('');
  const [selectedItems, setSelectedItems] = useState([]); // [{ part, quantity }]

  // Summary stats
  const totalTransactions = transactions.length;
  const totalRevenue = transactions.reduce((sum, t) => sum + t.total, 0);
  const avgTransaction = totalTransactions > 0 ? (totalRevenue / totalTransactions).toFixed(2) : '0.00';

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    if (!searchTerm) return transactions;
    const lower = searchTerm.toLowerCase();
    return transactions.filter(t =>
      t.id.toLowerCase().includes(lower) ||
      t.customer.toLowerCase().includes(lower) ||
      t.payment.toLowerCase().includes(lower)
    );
  }, [transactions, searchTerm]);

  // Add item to cart
  const addItemToCart = (part) => {
    if (part.stock === 0) return;
    setSelectedItems(prev => {
      const existing = prev.find(i => i.part.sku === part.sku);
      if (existing) {
        const newQty = existing.quantity + 1;
        if (newQty > part.stock) return prev;
        return prev.map(i => i.part.sku === part.sku ? { ...i, quantity: newQty } : i);
      }
      return [...prev, { part, quantity: 1 }];
    });
  };

  // Update quantity
  const updateQuantity = (sku, qty) => {
    const item = selectedItems.find(i => i.part.sku === sku);
    if (qty > item.part.stock) return;
    if (qty < 1) {
      setSelectedItems(prev => prev.filter(i => i.part.sku !== sku));
    } else {
      setSelectedItems(prev => prev.map(i => i.part.sku === sku ? { ...i, quantity: qty } : i));
    }
  };

  // Calculate total
  const cartTotal = selectedItems.reduce((sum, item) => sum + (item.part.price * item.quantity), 0);

  // Save new transaction
  const handleSaveTransaction = () => {
    if (selectedItems.length === 0) return;

    const customer = customerName.trim() || 'Walk-in Customer';

    const newTx = {
      id: `TXN-2025-${String(transactions.length + 1).padStart(3, '0')}`,
      date: new Date().toLocaleDateString('en-US'),
      customer,
      payment: 'Cash',
      total: cartTotal,
      status: 'Completed',
      items: selectedItems.map(i => ({
        name: i.part.name,
        qty: i.quantity,
        price: i.part.price
      }))
    };

    // Update inventory stock
    setInventory(prev => prev.map(part => {
      const sold = selectedItems.find(i => i.part.sku === part.sku);
      if (sold) {
        return { ...part, stock: part.stock - sold.quantity };
      }
      return part;
    }));

    // Add transaction
    setTransactions([newTx, ...transactions]);

    // Show success
    setSuccessMessage(`Transaction completed for ${customer}! Total: ₱${cartTotal.toFixed(2)}`);
    setShowSuccessModal(true);

    // Reset modal
    setIsModalOpen(false);
    setCustomerName('');
    setSelectedItems([]);

    // Auto-hide success modal
    setTimeout(() => setShowSuccessModal(false), 3000);
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header Section - Responsive Layout */}
      <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-start gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Transactions</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">View and manage payment transactions</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-5 py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-sm w-full md:w-auto"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Transaction
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Transactions</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{totalTransactions}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">All time</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">₱{totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">From completed sales</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Transaction</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">₱{avgTransaction}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Per sale</p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All Transactions</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{filteredTransactions.length} records</p>
            </div>
            <div className="relative max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ID, customer, or payment..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTransactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{tx.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{tx.date}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{tx.customer}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{tx.payment}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">₱{tx.total}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      tx.status === 'Completed'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                    }`}>
                      {tx.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setSelectedTx(tx)}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTx && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full p-6 relative border border-gray-200 dark:border-gray-700">
            <button onClick={() => setSelectedTx(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Transaction Receipt</h2>

            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-gray-500 dark:text-gray-400">ID:</span> <span className="font-semibold">{selectedTx.id}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">Date:</span> <span className="font-semibold">{selectedTx.date}</span></div>
              </div>
              <div><span className="text-gray-500 dark:text-gray-400">Customer:</span> <span className="font-semibold">{selectedTx.customer}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Payment:</span> <span className="font-semibold">{selectedTx.payment}</span></div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="font-semibold mb-3">Purchased Items</p>
                {selectedTx.items.map((item, i) => (
                  <div key={i} className="flex justify-between py-2">
                    <span>{item.qty} × {item.name}</span>
                    <span className="font-medium">₱{(item.qty * item.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-300 dark:border-gray-600">
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-blue-600 dark:text-blue-400">₱{selectedTx.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Transaction Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">New Transaction</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Item Selection */}
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Customer Name (Optional)</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Walk-in Customer"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Parts</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {inventory.map((part) => (
                    <div
                      key={part.sku}
                      className="flex justify-between items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{part.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          ₱{part.price.toFixed(2)} • Stock:{' '}
                          <span className={part.stock <= 10 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                            {part.stock}
                          </span>
                          {part.stock <= 10 && ' (Low)'}
                        </p>
                      </div>
                      <button
                        onClick={() => addItemToCart(part)}
                        disabled={part.stock === 0}
                        className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition"
                      >
                        {part.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Cart Summary */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cart Summary</h3>
                {selectedItems.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">No items added yet</p>
                ) : (
                  <div className="space-y-4">
                    {selectedItems.map((item) => (
                      <div key={item.part.sku} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{item.part.name}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">₱{item.part.price} each</p>
                          </div>
                          <button
                            onClick={() => setSelectedItems(prev => prev.filter(i => i.part.sku !== item.part.sku))}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <input
                            type="number"
                            min="1"
                            max={item.part.stock}
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.part.sku, parseInt(e.target.value) || 1)}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-center"
                          />
                          <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                            ₱{(item.part.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}

                    <div className="pt-6 border-t border-gray-300 dark:border-gray-600">
                      <div className="flex justify-between text-2xl font-bold text-gray-900 dark:text-white">
                        <span>Total Amount</span>
                        <span className="text-blue-600 dark:text-blue-400">₱{cartTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTransaction}
                disabled={selectedItems.length === 0}
                className="px-8 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition shadow-sm"
              >
                Complete Transaction
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowSuccessModal(false)}></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full mx-4 text-center animate-fadeIn">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Transaction Completed!
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
}

export default TransactionsPage;